// 2.5D navigation grid built from the collision voxels: one node per standable (cell, floor height),
// so stairs and several storeys work. Nodes only exist where the Character fits, which makes A* paths
// directly walkable by the same controller. Used for tap-to-walk, room jumps and pipeline QA.

/**
 * @param {import('./character.js').Character} character - supplies grid, agent size and overlap test
 * @param {object} [opts]
 * @param {number} [opts.cell] - cell size in m (default 2 voxels)
 */
export function buildNavGrid(character, opts = {}) {
    const g = character.grid;
    const a = character.agent;
    const cell = opts.cell ?? g.res * 2;
    const cx = Math.ceil((g.max[0] - g.min[0]) / cell);
    const cz = Math.ceil((g.max[2] - g.min[2]) / cell);
    const needFree = Math.ceil(a.height / g.res);

    const nodes = [];           // {x, y, z, i, j}
    const byCell = new Map();   // cell key -> node ids, sorted by height
    for (let j = 0; j < cz; j++) {
        for (let i = 0; i < cx; i++) {
            const x = g.min[0] + (i + 0.5) * cell, z = g.min[2] + (j + 0.5) * cell;
            const ix = g.ix(x), iz = g.iz(z);
            // Scan the column bottom-up for solid voxels with enough free space above
            let free = 0;
            for (let iy = g.ny - 1; iy >= 0; iy--) {
                if (!g.solid(ix, iy, iz)) { free++; continue; }
                if (free >= needFree) {
                    const y = g.top(iy);
                    if (!character.overlaps(x, y, z)) {
                        const id = nodes.length;
                        nodes.push({ x, y, z, i, j });
                        const key = j * cx + i;
                        if (!byCell.has(key)) byCell.set(key, []);
                        byCell.get(key).push(id);
                    }
                }
                free = 0;
            }
        }
    }

    // Neighbours: 8-connected, height change within one step, no corner cutting
    const step = a.stepHeight + 1e-4;
    const at = (i, j, y) => {
        if (i < 0 || j < 0 || i >= cx || j >= cz) return -1;
        const ids = byCell.get(j * cx + i);
        if (!ids) return -1;
        let best = -1, bd = step;
        for (const id of ids) {
            const d = Math.abs(nodes[id].y - y);
            if (d <= bd) { bd = d; best = id; }
        }
        return best;
    };
    const links = nodes.map(() => []);
    nodes.forEach((n, id) => {
        for (let dj = -1; dj <= 1; dj++) {
            for (let di = -1; di <= 1; di++) {
                if (!di && !dj) continue;
                const m = at(n.i + di, n.j + dj, n.y);
                if (m < 0) continue;
                if (di && dj && (at(n.i + di, n.j, n.y) < 0 || at(n.i, n.j + dj, n.y) < 0)) continue;
                links[id].push(m);
            }
        }
    });

    return new NavGrid(nodes, links, byCell, cell, cx, cz, g.min, step);
}

class Heap {
    constructor() { this.a = []; }
    push(item, pri) {
        const a = this.a; a.push([pri, item]);
        let i = a.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (a[p][0] <= a[i][0]) break;
            [a[p], a[i]] = [a[i], a[p]]; i = p;
        }
    }
    pop() {
        const a = this.a, top = a[0], last = a.pop();
        if (a.length) {
            a[0] = last;
            let i = 0;
            for (;;) {
                const l = 2 * i + 1, r = l + 1;
                let m = i;
                if (l < a.length && a[l][0] < a[m][0]) m = l;
                if (r < a.length && a[r][0] < a[m][0]) m = r;
                if (m === i) break;
                [a[m], a[i]] = [a[i], a[m]]; i = m;
            }
        }
        return top[1];
    }
    get size() { return this.a.length; }
}

export class NavGrid {
    constructor(nodes, links, byCell, cell, cx, cz, min, step) {
        Object.assign(this, { nodes, links, byCell, cell, cx, cz, min, step });
    }

    /** Nearest node to a world point, preferring the floor just below it. */
    nearest(x, y, z, maxDist = 1.5) {
        const ci = Math.floor((x - this.min[0]) / this.cell), cj = Math.floor((z - this.min[2]) / this.cell);
        const rings = Math.ceil(maxDist / this.cell);
        let best = -1, bd = Infinity;
        for (let r = 0; r <= rings; r++) {
            for (let j = cj - r; j <= cj + r; j++) {
                for (let i = ci - r; i <= ci + r; i++) {
                    if (Math.max(Math.abs(i - ci), Math.abs(j - cj)) !== r) continue;
                    const ids = this.byCell.get(j * this.cx + i);
                    if (!ids || i < 0 || j < 0 || i >= this.cx || j >= this.cz) continue;
                    for (const id of ids) {
                        const n = this.nodes[id];
                        // Floors above the query point are much less likely to be meant
                        const dy = n.y > y + 0.3 ? (n.y - y) * 4 : Math.abs(y - n.y) * 0.5;
                        const d = Math.hypot(n.x - x, n.z - z) + dy;
                        if (d < bd) { bd = d; best = id; }
                    }
                }
            }
            if (best >= 0 && r * this.cell > bd) break;
        }
        return best;
    }

    /** A* from node a to node b. Returns node ids or null. */
    path(a, b) {
        if (a < 0 || b < 0) return null;
        const N = this.nodes, goal = N[b];
        const g = new Map([[a, 0]]), came = new Map();
        const open = new Heap();
        const h = id => Math.hypot(N[id].x - goal.x, N[id].y - goal.y, N[id].z - goal.z);
        open.push(a, h(a));
        const closed = new Set();
        while (open.size) {
            const cur = open.pop();
            if (cur === b) {
                const out = [cur];
                let c = cur;
                while (came.has(c)) { c = came.get(c); out.push(c); }
                return out.reverse();
            }
            if (closed.has(cur)) continue;
            closed.add(cur);
            for (const nb of this.links[cur]) {
                const cost = g.get(cur) + Math.hypot(N[nb].x - N[cur].x, N[nb].y - N[cur].y, N[nb].z - N[cur].z);
                if (cost < (g.get(nb) ?? Infinity)) {
                    g.set(nb, cost);
                    came.set(nb, cur);
                    open.push(nb, cost + h(nb));
                }
            }
        }
        return null;
    }

    /** Node ids reachable from a (flood fill). */
    reachable(a) {
        const seen = new Uint8Array(this.nodes.length);
        if (a < 0) return seen;
        const q = [a];
        seen[a] = 1;
        while (q.length) {
            const c = q.pop();
            for (const nb of this.links[c]) if (!seen[nb]) { seen[nb] = 1; q.push(nb); }
        }
        return seen;
    }

    /**
     * Turn a node path into a short list of world waypoints: drop nodes while the straight line
     * between the kept ones stays on walkable cells at a similar height.
     */
    smooth(ids) {
        if (!ids || ids.length < 3) return (ids || []).map(id => this._pt(id));
        const out = [ids[0]];
        let anchor = 0;
        for (let k = 2; k < ids.length; k++) {
            if (!this._clear(ids[anchor], ids[k])) {
                out.push(ids[k - 1]);
                anchor = k - 1;
            }
        }
        out.push(ids[ids.length - 1]);
        return out.map(id => this._pt(id));
    }

    _pt(id) {
        const n = this.nodes[id];
        return [n.x, n.y, n.z];
    }

    _clear(a, b) {
        const A = this.nodes[a], B = this.nodes[b];
        const len = Math.hypot(B.x - A.x, B.z - A.z);
        const steps = Math.ceil(len / (this.cell * 0.25));
        let y = A.y;
        for (let s = 1; s < steps; s++) {
            const t = s / steps;
            const x = A.x + (B.x - A.x) * t, z = A.z + (B.z - A.z) * t;
            const i = Math.floor((x - this.min[0]) / this.cell), j = Math.floor((z - this.min[2]) / this.cell);
            const ids = this.byCell.get(j * this.cx + i);
            if (!ids) return false;
            let ok = false;
            for (const id of ids) {
                if (Math.abs(this.nodes[id].y - y) <= this.step) { y = this.nodes[id].y; ok = true; break; }
            }
            // Nodes only exist where the whole body fits, so walkable cells along the line suffice
            if (!ok) return false;
        }
        return true;
    }
}
